/** Filtros del historial de lead/cliente — comunicaciones ≠ eventos de sistema. */

export const HISTORIAL_FILTRO_IDS = ['whatsapp', 'mail', 'llamadas', 'citas', 'notas', 'sistema'];

/** Chips del historial (ficha cliente + captaciones). */
export const HISTORIAL_FILTRO_CHIPS = [
  { id: 'todos', label: 'Todos', reset: true },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'mail', label: 'Mail' },
  { id: 'llamadas', label: 'Llamadas' },
  { id: 'citas', label: 'Citas' },
  { id: 'notas', label: 'Notas' },
  { id: 'sistema', label: 'Sistema' },
];

const TIPOS_COMUNICACION = new Set([
  'whatsapp_enviado',
  'whatsapp_recibido',
  'whatsapp_respuesta',
  'whatsapp_lead',
  'whatsapp', // legacy captación (saliente)
  'email_enviado',
  'email_recibido',
  'email',
  'llamada',
  'llamada_ana',
  'contacto', // captación: canal concreta la vía
]);

const TIPOS_SISTEMA = new Set([
  'lead_creado',
  'lead_asignado',
  'lead_comercial_sugerido',
  'lead_reasignado',
  'lead_vinculado_cliente_nuevo',
  'lead_eliminado',
  'tarea_flujo',
  'tarea_vencida',
  'whatsapp_ana',
  'etapa_cambio',
  'cambio_etapa',
  'conversion_cliente',
  'visita_confirmada',
  'cliente_creado',
  'demanda_creada',
  'cliente_vinculado_cualificacion',
  'sistema',
]);

export function parseHistorialMetadata(h) {
  if (!h?.metadata) return {};
  if (typeof h.metadata === 'object') return h.metadata;
  try {
    return JSON.parse(h.metadata);
  } catch {
    return {};
  }
}

/** Cita de captación (incl. anulaciones ligadas a agenda). */
export function esHistorialCitaCaptacion(h) {
  const tipo = String(h?.tipo || '').toLowerCase();
  if (tipo === 'cita' || tipo === 'cita_creada') return true;
  const meta = parseHistorialMetadata(h);
  if (meta.origen === 'cita_anulada' || meta.origen === 'visita_no_hecha') return true;
  const cont = String(h?.contenido || '').toLowerCase();
  if (cont.includes('cita de captación anulada') || cont.includes('visita de captación no realizada')) return true;
  // agenda_id solo no basta: las anulaciones ya van por origen; citas nuevas sí lo llevan
  if (tipo === 'cita' && meta.agenda_id) return true;
  return false;
}

/** Eventos automáticos del CRM — no WA/mail/llamadas aunque las ejecute Ana. */
export function esHistorialSistema(h) {
  const tipo = String(h?.tipo || '');
  const t = tipo.toLowerCase();
  if (TIPOS_COMUNICACION.has(tipo) || TIPOS_COMUNICACION.has(t)) return false;
  if (esHistorialCitaCaptacion(h)) return false;
  if (TIPOS_SISTEMA.has(tipo) || TIPOS_SISTEMA.has(t)) return true;
  if (t.startsWith('ana_')) return true;
  return false;
}

/**
 * Tipo visual unificado (ficha + captación) para icono y punto WA.
 * Captación legacy: tipo `whatsapp` / `contacto`+canal → mismos tiles que ficha.
 */
export function tipoVisualHistorial(h) {
  const tipo = String(h?.tipo || '').toLowerCase();
  const canal = String(h?.canal || '').toLowerCase();

  if (['whatsapp_recibido', 'whatsapp_respuesta', 'whatsapp_lead'].includes(tipo)) return 'whatsapp_recibido';
  if (['whatsapp_enviado', 'whatsapp'].includes(tipo)) return 'whatsapp_enviado';
  if (tipo === 'contacto' && canal === 'whatsapp') return 'whatsapp_enviado';

  if (tipo === 'email_recibido') return 'email_recibido';
  if (['email_enviado', 'email'].includes(tipo) || (tipo === 'contacto' && canal === 'email')) return 'email_enviado';

  if (['llamada', 'llamada_ana'].includes(tipo) || (tipo === 'contacto' && canal === 'llamada')) return tipo === 'llamada_ana' ? 'llamada_ana' : 'llamada';

  if (esHistorialCitaCaptacion(h)) return 'cita_creada';
  if (tipo === 'nota' || tipo === 'nota_manual') return 'nota';
  if (tipo === 'contacto' && canal === 'presencial') return 'nota';

  if (['etapa_cambio', 'cambio_etapa', 'sistema'].includes(tipo) || esHistorialSistema(h)) return 'etapa_cambio';
  return 'nota';
}

/** Punto WA: saliente amarillo / entrante rojo (acepta tipos captación). */
export function tipoParaPuntoWa(h) {
  return tipoVisualHistorial(h);
}

/** Solo el nombre de pila («Josefa Muñoz» → «Josefa»). */
export function primerNombreAgente(nombre) {
  const n = String(nombre || "").trim();
  if (!n) return "";
  const first = n.split(/\s+/)[0].replace(/[(),]/g, "").trim();
  return first;
}

function parseHistorialMetaLocal(h) {
  let meta = h?.metadata;
  if (typeof meta === "string") {
    try {
      meta = JSON.parse(meta);
    } catch {
      meta = {};
    }
  }
  return meta && typeof meta === "object" ? meta : {};
}

/**
 * Quién realizó la acción (usuario CRM / agente).
 * Preferimos actor_* frente al agente_id del registro (a menudo el titular, no quien actuó).
 */
export function resolverNombreActorHistorial(h) {
  const meta = parseHistorialMetaLocal(h);
  const raw =
    h?.actor_nombre ||
    h?.actor ||
    meta?.actor_nombre ||
    meta?.actor ||
    h?.agente_nombre ||
    meta?.agente_nombre ||
    "";
  return String(raw || "").trim();
}

function tituloYaIncluyeAutor(titulo, nombrePila) {
  const t = String(titulo || "");
  const n = String(nombrePila || "").trim();
  if (!n) return false;
  if (/\b(realizado\s+por|por)\s+\S+/i.test(t)) return true;
  // Formato WA: «Josefa WhatsApp enviado»
  if (new RegExp(`^${n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+`, "i").test(t)) return true;
  return false;
}

/**
 * Añade el autor al título del historial.
 * - «Lead creado» → «Lead creado por Juan»
 * - «Reasignación de agente (cliente)» → «… realizado por Juan»
 * WhatsApp saliente sigue con `etiquetaWhatsAppEnviadoHistorial` (formato propio).
 */
export function etiquetaHistorialConAutor(tituloBase, h) {
  const base = String(tituloBase || "").trim();
  if (!base) return base;
  const first = primerNombreAgente(resolverNombreActorHistorial(h));
  if (!first || /^(sistema|crm)$/i.test(first)) return base;
  if (tituloYaIncluyeAutor(base, first)) return base;

  // Participios / «… creado|enviado|…» → «… por Juan»
  if (
    /\b(creado|creada|enviado|enviada|recibido|recibida|asignado|asignada|reasignado|reasignada|eliminado|eliminada|movido|movida|completado|completada|registrado|registrada|confirmado|confirmada|anulado|anulada|programado|programada)\b/i.test(
      base
    )
  ) {
    return `${base} por ${first}`;
  }
  // Sustantivos («Reasignación…», «Flujo de tareas»…) → «… realizado por Juan»
  return `${base} realizado por ${first}`;
}

/**
 * Título historial WA saliente: «Josefa WhatsApp enviado».
 * Sin agente → «WhatsApp enviado».
 */
export function etiquetaWhatsAppEnviadoHistorial(h) {
  const meta = parseHistorialMetaLocal(h);
  const raw =
    h?.agente_nombre ||
    h?.actor_nombre ||
    h?.actor ||
    meta?.agente_nombre ||
    meta?.actor_nombre ||
    "";
  const first = primerNombreAgente(raw);
  if (!first || /^sistema$/i.test(first)) return "WhatsApp enviado";
  return `${first} WhatsApp enviado`;
}

export function categoriasHistorial(h) {
  const tipo = String(h?.tipo || '');
  const t = tipo.toLowerCase();
  const canal = String(h?.canal || '').toLowerCase();
  const cats = [];
  const visual = tipoVisualHistorial(h);

  if (['whatsapp_recibido', 'whatsapp_respuesta', 'whatsapp_lead', 'whatsapp_enviado', 'whatsapp'].includes(t)
    || visual === 'whatsapp_enviado' || visual === 'whatsapp_recibido') {
    // No meter en WA un evento de sistema aunque lleve canal whatsapp
    if (!esHistorialSistema(h) && t !== 'sistema') cats.push('whatsapp');
  }
  if (t === 'email_enviado' || t === 'email_recibido' || t === 'email' || visual === 'email_enviado' || visual === 'email_recibido') {
    if (!esHistorialSistema(h)) cats.push('mail');
  }
  if (t === 'llamada' || t === 'llamada_ana' || visual === 'llamada' || visual === 'llamada_ana') {
    if (!esHistorialSistema(h)) cats.push('llamadas');
  }
  if (t === 'cita_creada' || t === 'cita' || esHistorialCitaCaptacion(h)) cats.push('citas');
  if (t === 'oferta_registrada') cats.push('citas');
  if (t === 'cualificacion_visita') cats.push('citas');
  if (t === 'sin_interes') cats.push('citas');
  if (t === 'nota' || t === 'nota_manual' || (t === 'contacto' && canal === 'presencial')) cats.push('notas');

  if (esHistorialSistema(h)) cats.push('sistema');

  return cats;
}

/** Cambio de titular / vencimiento: trazabilidad crítica; no ocultar solo por excluir «Sistema». */
const TIPOS_TRAZABILIDAD_TITULAR = new Set([
  'lead_reasignado',
  'tarea_vencida',
  'cliente_marcado_frio',
]);

export function historialPasaFiltros(h, incl, excl) {
  if (!incl?.length && !excl?.length) return true;
  const tipo = String(h?.tipo || '').toLowerCase();
  const cats = categoriasHistorial(h);

  if (TIPOS_TRAZABILIDAD_TITULAR.has(tipo)) {
    // Con filtro positivo (p. ej. solo WhatsApp): respetarlo.
    if (incl?.length) return cats.some((cat) => incl.includes(cat));
    // Con exclusiones: ignorar la de «sistema» para estos tipos.
    if (excl?.length) {
      const otras = excl.filter((cat) => cat !== 'sistema');
      if (otras.length && cats.some((cat) => otras.includes(cat))) return false;
      return true;
    }
    return true;
  }

  if (excl?.length && cats.some((cat) => excl.includes(cat))) return false;
  if (incl?.length) return cats.some((cat) => incl.includes(cat));
  return true;
}

export function categoriaTipoHistorial(item) {
  const cats = categoriasHistorial(item);
  if (cats.includes('whatsapp')) return 'whatsapp';
  if (cats.includes('mail')) return 'email';
  if (cats.includes('llamadas')) return 'llamada';
  if (cats.includes('notas')) return 'nota';
  if (cats.includes('citas')) return 'cita';
  return 'sistema';
}
